import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { authLogin } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { loginSuccess } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError("Please enter email and password");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const user = await authLogin(email.trim(), password);
      loginSuccess(user);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen w-full flex items-center justify-center bg-[#F7F5F3]" data-testid="login-page">
      <div className="w-full max-w-sm p-8">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Organon"
            className="h-9 w-9 rounded-sm"
          />
          <span className="text-2xl font-black tracking-tight text-brand" style={{ fontFamily: "Chivo, sans-serif" }}>
            Organon
          </span>
        </div>

        <h1
          className="text-xl font-bold text-center mb-1 text-brand"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          Log in
        </h1>
        <p className="text-sm text-gray-500 text-center mb-6">
          Enter your credentials to continue
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div
              data-testid="login-error"
              className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2"
            >
              {error}
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
              Email
            </label>
            <input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-sm text-sm focus:border-brand-rose focus:ring-1 focus:ring-brand-rose outline-none transition-all"
              autoComplete="email"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
              Password
            </label>
            <input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-sm text-sm focus:border-brand-rose focus:ring-1 focus:ring-brand-rose outline-none transition-all"
              autoComplete="current-password"
            />
          </div>

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-brand text-white hover:bg-brand-dark rounded-sm px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-40"
          >
            {loading ? "Logging in..." : "Log in"}
          </button>
        </form>

        <p className="text-sm text-gray-500 text-center mt-6">
          Don't have an account?{" "}
          <Link to="/signup" className="text-brand-rose font-semibold hover:underline" data-testid="go-to-signup">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
